'use client';

import {
  useState,
  useMemo,
  type FC,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import Link from 'next/link';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import {
  Alert,
  Flex,
  Input,
  Button,
  ButtonGroup,
  Select,
  SelectItem,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { createNewOwner } from '@/_api/admin-companies';
import { validateEmail } from '@/_utils/validations';
import { getIsEmailTaken } from '@/_api/admin-users';
import { useDebounce } from '@/_hooks/useDebounce';
import type { NewOwnerFormProps } from './NewOwnerForm.types';
import type { User } from '@/_types/models/user';
import { deformatPhoneNumber } from '@/_utils/phoneNumbers';

export const NewOwnerForm: FC<NewOwnerFormProps> = ({
  onClose,
  setNewOwners,
}) => {
  const { firmUserOptions } = useContext(FirmSettingsContext);
  const { selectedCompany } = useContext(CompanyContext);
  const { addToast } = useContext(ToastContext);
  const { user, token } = useContext(UserContext);

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [advisor, setAdvisor] = useState<string>(user?.id?.toString() || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isEmailTaken, setIsEmailTaken] = useState<boolean>(true);

  const onFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFirstName(event.target.value);
  const onLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setLastName(event.target.value);
  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value?.trim()?.toLowerCase());
  const onPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPhone(event.target.value);
  const onFirmUserChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setAdvisor(event.target.value);
  };

  const isEmailValid: boolean = useMemo(() => validateEmail(email), [email]);

  const defaultAttrs = useMemo(
    () => ({
      token,
      companyId: selectedCompany?.id!,
      firstName,
      lastName,
      email,
      phoneNumber: deformatPhoneNumber(phone),
      advisor: parseInt(advisor),
    }),
    [token, advisor, phone, lastName, firstName, email, selectedCompany?.id],
  );

  // Removing firm user that are not in the same firm group as the selected entity
  const firmUserFilteredOptions: Array<SelectItem> = useMemo(() => {
    const users = firmUserOptions.filter(
      (user) =>
        !user?.firm_group_id ||
        user?.firm_group_id === selectedCompany?.user?.firm_group?.id,
    );
    return users.map((user) => ({
      name: user.name,
      value: user.value,
    }));
  }, [firmUserOptions, selectedCompany?.user?.firm_group?.id]);

  const onAddOwner = async () => {
    try {
      setIsLoading(true);

      const newOwner = await createNewOwner({
        ...defaultAttrs,
        copyPreviousMessages: false,
        transferPreviousMessages: false,
      });

      if (newOwner?.error) {
        if (newOwner.error.includes('email would belong to multiple firms')) {
          addToast(
            'error',
            'Email is already taken. Learn how to resolve this issue <a href="https://whimsical-split-479.notion.site/Handling-a-Client-Formerly-at-Another-Firm-3aa64306c9444d80b8cf815aadeaac96" target="_blank">here</a>.',
            { error: newOwner.error },
          );
        } else {
          addToast('error', null, { error: newOwner.error });
        }

        return;
      }

      if (newOwner?.id) {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setAdvisor('');

        const newOwners: Array<User> = [
          ...(selectedCompany?.all_owners || []),
          newOwner as User,
        ];

        if (newOwner.paying) {
          setNewOwners(newOwners);
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmail: boolean = useMemo(() => validateEmail(email), [email]);
  const isDisabled: boolean =
    isLoading ||
    !firstName ||
    !lastName ||
    (!!email && !isValidEmail && !isEmailTaken);

  const debouncedEmail: string = useDebounce(email, 200);

  const _validateEmailAddress = useCallback(
    async (email?: string): Promise<boolean> => {
      const isValidEmail: boolean = validateEmail(email);

      if (!isValidEmail) return false;

      try {
        const { is_available } = await getIsEmailTaken({
          token,
          email: email!,
        });

        return !is_available;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    [token],
  );

  const validateEmailAddress = useCallback(
    async (email?: string) => {
      const isValidEmail: boolean = await _validateEmailAddress(email);

      setIsEmailTaken(isValidEmail);
    },
    [_validateEmailAddress],
  );

  useEffect(() => {
    if (!!debouncedEmail) {
      validateEmailAddress(debouncedEmail);
    }
  }, [validateEmailAddress, debouncedEmail]);

  return (
    <Flex gap={6} isColumn={true}>
      <Flex gap={6}>
        <Input
          label="First Name"
          onChange={onFirstNameChange}
          placeholder="John"
          value={firstName}
        />
        <Input
          label="Last Name"
          onChange={onLastNameChange}
          placeholder="Doe"
          value={lastName}
        />
        <Select
          label="Assigned Firm User"
          items={firmUserFilteredOptions}
          selectedValue={advisor}
          onChange={onFirmUserChange}
        />
      </Flex>
      <Flex gap={6}>
        <Input
          label="Email"
          placeholder="john@example.com"
          iconRight={!isEmailTaken && isEmailValid ? 'check-circle' : undefined}
          iconRightColor="success"
          helperText={
            isEmailValid && isEmailTaken ? 'This email already exists' : ''
          }
          helperTextColor="critical"
          onChange={onEmailChange}
          value={email}
        />
        <Input
          label="Phone Number"
          mask="(___) ___-____"
          placeholder="(000) 000-0000"
          onChange={onPhoneChange}
          value={phone}
        />
      </Flex>
      {isEmailValid && isEmailTaken && (
        <Alert type="danger">
          This email already exists. You can search by email on the{' '}
          <Link href="/clients">users page</Link> to see who has this email.
        </Alert>
      )}
      <ButtonGroup align="right">
        <Button intent="secondary" value="Cancel" onClick={onClose} />
        <Button
          intent="primary"
          value="Add User"
          onClick={onAddOwner}
          isDisabled={isDisabled}
        />
      </ButtonGroup>
    </Flex>
  );
};
